import { useEffect, useState } from "react";
import { doc, onSnapshot, setDoc } from "firebase/firestore";
import { HouseholdPushStatus } from "./HouseholdPushStatus";
import "./notification-controls.css";
import { db } from "../lib/firebase";
import { testThisDevice, pushErrorHelp, type PushState, type PushTest } from "../lib/notifications";

export type NotificationPreferenceKey =
  | "familyPing"
  | "homeHuntStart"
  | "homeHuntWinner"
  | "faceOffReady"
  | "weeklyGameResults"
  | "calendarEventReminder"
  | "calendarSyncProblem"
  | "financeConnectionProblem"
  | "financeLargeTransaction"
  | "financeIncomeAlert"
  | "financeTransactionsReady"
  | "financeTradeActivity"
  | "financeDividend"
  | "financeDailySummary"
  | "financeWeeklySummary"
  | "countdownReminder"
  | "householdTaskReminder";

type Preferences=Record<NotificationPreferenceKey,boolean>;
const defaults:Preferences={
  familyPing:true,homeHuntStart:true,homeHuntWinner:true,
  faceOffReady:false,weeklyGameResults:true,
  calendarEventReminder:false,calendarSyncProblem:false,
  financeConnectionProblem:false,financeLargeTransaction:false,financeIncomeAlert:false,financeTransactionsReady:false,
  financeTradeActivity:false,financeDividend:false,financeDailySummary:false,financeWeeklySummary:false,
  countdownReminder:false,householdTaskReminder:false
};
const groups:Array<{id:string;title:string;summary:string;items:Array<{key:NotificationPreferenceKey;label:string;description:string;live:boolean}>}>= [
  {id:"family",title:"Family Check-In",summary:"Voluntary updates and check-in requests from your family",items:[
    {key:"familyPing",label:"Check-ins and requests",description:"Push alerts when family checks in or asks where you are.",live:true}
  ]},
  {id:"games",title:"Games",summary:"Home Hunt, Face-Off and results",items:[
    {key:"homeHuntStart",label:"Home Hunt begins",description:"The house is hiding somewhere in FamDashFun.",live:true},
    {key:"homeHuntWinner",label:"Home Hunt winner",description:"Someone found the house and earned the point.",live:true},
    {key:"faceOffReady",label:"Face-Off ready",description:"A new family question is available.",live:false},
    {key:"weeklyGameResults",label:"Weekly game results",description:"Sunday’s FamBucks breakdown and bragging-rights alert.",live:true}
  ]},
  {id:"calendar",title:"Calendar",summary:"Events and calendar connections",items:[
    {key:"calendarEventReminder",label:"Event reminders",description:"Upcoming family events and countdowns.",live:false},
    {key:"calendarSyncProblem",label:"Calendar needs attention",description:"A connected calendar stopped syncing.",live:false}
  ]},
  {id:"finance",title:"Finance",summary:"Eight focused money alerts",items:[
    {key:"financeConnectionProblem",label:"Connection needs attention",description:"Plaid or a connected institution requires action.",live:true},
    {key:"financeLargeTransaction",label:"Large transaction",description:"A posted purchase is $500 or more.",live:true},
    {key:"financeIncomeAlert",label:"Income received",description:"A tracked paycheck or other income deposit arrives.",live:true},
    {key:"financeTransactionsReady",label:"Transactions need review",description:"New income or expenses are ready to review.",live:true},
    {key:"financeTradeActivity",label:"Buy, sell or quantity change",description:"A holding position changes or closes.",live:true},
    {key:"financeDividend",label:"Dividend received",description:"Plaid reports a new investment dividend.",live:true},
    {key:"financeDailySummary",label:"Daily investment summary",description:"Regular-session and after-hours portfolio results.",live:true},
    {key:"financeWeeklySummary",label:"Weekly finance summary",description:"Weekly portfolio, income and expense totals.",live:true}
  ]},
  {id:"reminders",title:"Reminders",summary:"Countdowns, shopping and household tasks",items:[
    {key:"countdownReminder",label:"Countdown reminders",description:"An important countdown date is approaching.",live:false},
    {key:"householdTaskReminder",label:"Shopping and task reminders",description:"Assigned household items are due or still open.",live:false}
  ]}
];

function isStandaloneIosApp(){
  const standalone = window.matchMedia?.("(display-mode: standalone)").matches || (navigator as Navigator & { standalone?: boolean }).standalone === true;
  return standalone && /iPhone|iPad|iPod/i.test(navigator.userAgent);
}

export function NotificationPreferencesPanel({householdId,canManageFamily,uid,pushState,pushBusy,pushStatus,onEnable,onDisable}:{householdId:string;canManageFamily:boolean;uid:string;pushState:PushState;pushBusy:boolean;pushStatus:string;onEnable:()=>void;onDisable:()=>void}){
  const [preferences,setPreferences]=useState<Preferences>(defaults);
  const [saving,setSaving]=useState<NotificationPreferenceKey|null>(null);
  const [feedback,setFeedback]=useState("");
  const [testBusy,setTestBusy]=useState(false);
  const [test,setTest]=useState<PushTest|null>(null);
  const [testStatus,setTestStatus]=useState("");
  async function runTest(checkOnly:boolean){
    setTestBusy(true);setTest(null);
    setTestStatus(checkOnly ? "Checking the last result…" : "Checking this device registration. Keep this page open…");
    try {
      const result=await testThisDevice(checkOnly,()=>{if(!checkOnly)setTestStatus("Device registered. Lock your phone now; the server waits ten seconds before sending. Reopen this page and check the last result afterward.");});
      setTest(result);
      setTestStatus(!result ? "No test has been run for this device yet." : result.status==="accepted" ? "Push service accepted the test. This does not confirm a lock-screen banner; check your phone’s notification history." : result.status==="waiting" ? "The server is waiting to send. Check the last result again in a few seconds." : result.status==="cancelled" ? "Test cancelled because this device registration was removed." : "The push service rejected the test. Diagnostic details are below.");
    } catch(error) {
      const code=String((error as {code?:string})?.code||"");
      setTestStatus(code==="functions/resource-exhausted" ? "Wait one minute between tests. Use Check last result instead." : code==="functions/not-found" ? "The device-test service is not deployed yet." : "The device check did not complete. Confirm alerts are allowed, then try Check last result. Registration or network access may be failing.");
    } finally {setTestBusy(false);}
  }
  useEffect(()=>onSnapshot(doc(db,"users",uid),snapshot=>{
    setPreferences({...defaults,...(snapshot.data()?.notificationPreferences||{})});
  },()=>setFeedback("Notification choices are unavailable right now.")),[uid]);

  async function toggle(key:NotificationPreferenceKey){
    const next={...preferences,[key]:!preferences[key]};
    setPreferences(next);setSaving(key);setFeedback("");
    try{
      await setDoc(doc(db,"users",uid),{notificationPreferences:next},{merge:true});
      setFeedback("Notification choices saved.");
    }catch{
      setPreferences(preferences);setFeedback("That choice could not be saved.");
    }finally{setSaving(null);}
  }

  const blockedHelp=isStandaloneIosApp()
    ? "Push alerts are blocked on this iPhone. Open iPhone Settings → Notifications, find this Home Screen app, turn on Allow Notifications, then return here."
    : "Push alerts are blocked on this device. Open this browser or app’s notification settings, allow notifications, then return here.";

  return <div className="notification-preferences">
    <section aria-label="Device notification controls">
      <strong>Push alerts on this device</strong>
      <p className="connection-note">Applies only to this phone or browser.</p>
      <div className="notification-small-actions">
        {pushState==="on"
          ? <button className="notification-small-button" type="button" disabled={pushBusy} onClick={onDisable}>{pushBusy?"Updating…":"Turn off"}</button>
          : <button className="notification-small-button" type="button" disabled={pushBusy||pushState==="unsupported"||pushState==="blocked"} onClick={onEnable}>{pushBusy?"Updating…":"Turn on"}</button>}
        <button className="notification-small-button" type="button" disabled={testBusy||pushBusy||pushState==="blocked"||pushState==="unsupported"} onClick={()=>void runTest(false)}>Test device</button>
        <button className="notification-small-button" type="button" disabled={testBusy||pushBusy||pushState==="blocked"||pushState==="unsupported"} onClick={()=>void runTest(true)}>Last result</button>
      </div>
      <p className="connection-note">{pushState==="on" ? "Registration saved for this browser. Use Test device to check delivery." : "Device registration is not confirmed."}</p>
      {testStatus&&<p className="connection-note" role="status">{testStatus}</p>}
      {test&&<details><summary>Diagnostic details</summary>
        <p style={{overflowWrap:"anywhere"}}>Reference: {test.diagnosticId}<br/>Started: {new Date(test.startedAt).toLocaleString()}<br/>Result: {test.status}</p>
        {Object.entries(test.errorCounts||{}).map(([code,count])=><p key={code} style={{overflowWrap:"anywhere"}}><code>{code}</code> ({count})<br/>{pushErrorHelp(code)}</p>)}
      </details>}
    </section>
    {canManageFamily && householdId && <HouseholdPushStatus key={`${uid}:${householdId}`} householdId={householdId}/>}
    {pushState==="blocked"&&<p className="connection-note" role="alert"><strong>Push alerts are blocked on this device.</strong><br/>{blockedHelp}</p>}
    {pushState==="unsupported"&&<p className="connection-note">On iPhone or iPad, add FamDashFun to the Home Screen first, then open the installed app to enable alerts.</p>}
    <p className="auth-copy">Choose which alerts may reach you. Live alerts work now; “planned” choices are saved for features that are not sending alerts yet.</p>
    <div className="notification-preference-groups">
      {groups.map((group,index)=><details className="notification-preference-group" key={group.id} open={index===0}>
        <summary><span><b>{group.title}</b><small>{group.summary}</small></span><i aria-hidden="true">⌄</i></summary>
        <div>
          {group.items.map(item=><label className="notification-preference-row" key={item.key}>
            <span><strong>{item.label}</strong><small>{item.description}</small><em className={item.live?"live":"planned"}>{item.live?"Live now":"Planned"}</em></span>
            <input type="checkbox" checked={preferences[item.key]} disabled={saving===item.key} onChange={()=>void toggle(item.key)}/>
          </label>)}
        </div>
      </details>)}
    </div>
    {(pushStatus||feedback)&&<p className="calendar-connection-feedback" role="status">{feedback||pushStatus}</p>}
  </div>;
}

